import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
} from 'react';
import {
  DRAG_CONFIG,
  FULL_HEIGHT_PERCENTAGE,
  INITIAL_HEIGHT_PERCENTAGE,
  SPRING_CONFIG,
  TAP_THRESHOLD,
} from './BottomSheet.constants';
import styles from './BottomSheet.module.css';
import type { BottomSheetHandle, BottomSheetProps } from './BottomSheet.types';

const BottomSheet = forwardRef<BottomSheetHandle, BottomSheetProps>(
  (props, ref): JSX.Element => {
    const {
      children,
      isExpanded: externalIsExpanded,
      onExpandedChange,
      initialHeightPercent = INITIAL_HEIGHT_PERCENTAGE,
      className = '',
      showHandle = true,
      enableDrag = true,
      ...additionalProps
    } = props;
    // Internal state management
    const [internalIsExpanded, setInternalIsExpanded] =
      useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isAtTop, setIsAtTop] = useState<boolean>(true);
    const [screenHeight, setScreenHeight] = useState<number>(
      window.innerHeight
    );
    const contentRef = useRef<HTMLDivElement>(null);
    const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

    // Determine which state to use (external or internal)
    const isControlled = externalIsExpanded !== undefined;
    const isExpanded = isControlled ? externalIsExpanded : internalIsExpanded;
    const setIsExpanded = useCallback(
      (expanded: boolean) => {
        if (isControlled) {
          if (typeof onExpandedChange === 'function')
            onExpandedChange(expanded);
        } else setInternalIsExpanded(expanded);
      },
      [isControlled, onExpandedChange]
    );

    useEffect(() => {
      if (isControlled && typeof externalIsExpanded === 'boolean') {
        // When externally controlled, ensure internal state stays in sync
        // This is mainly for the spring animation to work correctly
        setInternalIsExpanded(externalIsExpanded);
      }
    }, [isControlled, externalIsExpanded]);

    const initialHeight = (screenHeight * initialHeightPercent) / 100; // % of screen height
    const fullHeight = (screenHeight * FULL_HEIGHT_PERCENTAGE) / 100; // Fully expanded
    const midHeight = screenHeight - initialHeight; // Partially expanded

    const handleResize = useCallback(() => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
      resizeTimeout.current = setTimeout(() => {
        setScreenHeight(window.innerHeight);
      }, 200);
    }, []);

    useEffect(() => {
      if (typeof window === 'undefined') return;

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    /**
     * Spring animation configuration for smooth bottom sheet movement
     * Uses react-spring for physics-based animations
     */
    const [spring, api] = useSpring(() => ({
      from: { transform: `translateY(${midHeight}px)` },
      config: SPRING_CONFIG,
    }));

    // Update spring position when heights change or external state changes
    useEffect(() => {
      const targetY = isExpanded ? fullHeight : midHeight;
      api.start({ transform: `translateY(${targetY}px)` });
    }, [midHeight, fullHeight, isExpanded, api]);

    /**
     * Drag binding for the handle at the top of the bottom sheet
     * Allows dragging the sheet down from any position when using the handle
     */
    const bindHandle = useDrag(
      ({ movement: [, my], down, velocity, direction }) => {
        if (!enableDrag) return;

        const currentY = parseFloat(
          spring.transform.get().match(/translateY\(([-\d.]+)px\)/)?.[1] || '0'
        );
        let newY = currentY + my;

        if (newY < fullHeight)
          newY = fullHeight + my * DRAG_CONFIG.yNormalisation;
        if (newY > midHeight)
          newY = midHeight + my * DRAG_CONFIG.yNormalisation;

        if (!down) {
          const velocityThreshold = DRAG_CONFIG.velocityThreshold;
          const distanceThreshold = DRAG_CONFIG.distanceThreshold;

          if (
            (velocity[1] > velocityThreshold || direction[1] > 0) &&
            my > distanceThreshold
          ) {
            newY = midHeight;
            setIsExpanded(false);
          } else {
            newY = fullHeight;
            setIsExpanded(true);
          }
        }

        api.start({ transform: `translateY(${newY}px)`, immediate: down });
      },
      { filterTaps: true }
    );

    /**
     * Drag binding for the main container
     * Only allows dragging when the sheet is at the top or not expanded
     */
    const bind = useDrag(
      ({ movement: [mx, my], down, velocity, direction, event, cancel }) => {
        if (!enableDrag) return;

        if (Math.abs(my) < TAP_THRESHOLD && !down) {
          cancel?.();
          return;
        }
        const isHorizontalGesture = Math.abs(mx) > Math.abs(my);
        if (isHorizontalGesture) return;

        if (
          (!isExpanded && direction[1] < 0) ||
          (isExpanded &&
            direction[1] > 0 &&
            contentRef.current &&
            contentRef.current.scrollTop === 0)
        ) {
          setIsDragging(down);
        } else {
          setIsDragging(false);
        }

        const target = event.target as HTMLElement;
        if (target.closest('.no-drag')) return;
        if (isExpanded && !isAtTop) return;

        const currentY = parseFloat(
          spring.transform.get().match(/translateY\(([-\d.]+)px\)/)?.[1] || '0'
        );
        let newY = currentY + my;

        // Apply resistance at boundaries
        if (newY < fullHeight)
          newY = fullHeight + my * DRAG_CONFIG.yNormalisation;
        if (newY > midHeight)
          newY = midHeight + my * DRAG_CONFIG.yNormalisation;

        if (!down) {
          const velocityThreshold = DRAG_CONFIG.velocityThreshold;
          const distanceThreshold = DRAG_CONFIG.distanceThreshold;

          if (
            (velocity[1] > velocityThreshold || direction[1] > 0) &&
            my > distanceThreshold
          ) {
            newY = midHeight;
            setIsExpanded(false);
          } else {
            newY = fullHeight;
            setIsExpanded(true);
          }
        }

        api.start({ transform: `translateY(${newY}px)`, immediate: down });
      },
      { filterTaps: true }
    );

    const handleScroll = useCallback(() => {
      if (contentRef.current) {
        const { scrollTop } = contentRef.current;
        const isAtTopNow = scrollTop === 0;
        setIsAtTop(isAtTopNow);
      }
    }, []);

    useEffect(() => {
      const contentEl = contentRef.current;
      if (!contentEl) return;

      contentEl.style.overflowY = isDragging ? 'hidden' : 'auto';
    }, [isDragging]);

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        if (contentRef.current) {
          contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
    }));

    return (
      <animated.div
        {...(enableDrag ? bind() : {})}
        {...additionalProps}
        style={spring}
        className={`${styles.container} ${className}`}
      >
        {showHandle && (
          <div
            {...(enableDrag ? bindHandle() : {})}
            className={styles.dragHandleContainer}
          >
            <div
              {...(enableDrag ? bindHandle() : {})}
              className={styles.dragHandle}
            />
          </div>
        )}
        <div
          className={styles.content}
          ref={contentRef}
          onScroll={handleScroll}
        >
          {children}
        </div>
      </animated.div>
    );
  }
);

export default BottomSheet;
